import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { money, num, plural, WORDS } from '../../lib/format';
import { Badge, ErrorState, PageHead, SearchBox, Stat } from '../../components/ui';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
  type LocalAccessors,
} from '../../components/ColumnFilter';
import { exportDay, exportNotes, type TableExportDoc } from '../../lib/tableExport';

type AgingAccount = Aging['accounts'][number];

/** حالة العميل من أقدم تأخيره — واحدةٌ في الشارة والفلتر والتصدير. */
const AGING_STATES = [
  { value: 'current', label: 'ضمن الموعد', kind: 'success' },
  { value: 'late', label: 'متأخر', kind: 'info' },
  { value: 'long', label: 'تأخير طويل', kind: 'warning' },
  { value: 'default', label: 'متعثّر', kind: 'danger' },
] as const;

function agingState(days: number): (typeof AGING_STATES)[number] {
  return AGING_STATES[days > 90 ? 3 : days > 60 ? 2 : days > 0 ? 1 : 0];
}

const AGING_FILTERS: FilterDef[] = [
  { key: 'name', label: 'العميل', kind: 'text', sortable: true },
  { key: 'outstanding', label: 'المستحق', kind: 'number', sortable: true },
  { key: 'oldestDays', label: 'أقدم تأخير (يوم)', kind: 'number', sortable: true },
  {
    key: 'state',
    label: 'الحالة',
    kind: 'enum',
    options: AGING_STATES.map((s) => ({ value: s.value, label: s.label })),
  },
];

const AGING_ACCESSORS: LocalAccessors<AgingAccount> = {
  state: (a) => agingState(a.oldestDays).value,
};

interface Aging {
  buckets: Array<{ key: string; label: string; kind: string; count: number; amount: number }>;
  totalOutstanding: number;
  accounts: Array<{ accountId: string; name: string; outstanding: number; oldestDays: number }>;
}

const BUCKET_COLOR: Record<string, string> = {
  current: 'var(--success)',
  b30: 'var(--info)',
  b60: 'var(--warning)',
  b90: 'var(--danger)',
};

export function AgingReport() {
  const aging = useQuery({
    queryKey: ['aging'],
    queryFn: () => api<Aging>('/finance/payments/aging'),
  });

  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const table = useLocalFilterTable({
    rows: aging.data?.accounts,
    defs: AGING_FILTERS,
    accessors: AGING_ACCESSORS,
    state,
    patch,
    search: search ? (a: AgingAccount) => matchesSearch(search, a.name) : undefined,
  });
  const accounts = table.rows;

  const buildExport = (): TableExportDoc => ({
    title: 'تقادم الذمم',
    sheets: [
      {
        title: 'العملاء حسب المستحق',
        period: { label: `كما في ${exportDay(new Date().toISOString())}` },
        sections: [
          {
            columns: [
              { key: 'name', label: 'العميل' },
              { key: 'outstanding', label: 'المستحق', kind: 'money', total: true },
              { key: 'oldestDays', label: 'أقدم تأخير (يوم)', kind: 'number' },
              { key: 'state', label: 'الحالة' },
            ],
            rows: accounts.map((a) => ({
              name: a.name,
              outstanding: a.outstanding,
              oldestDays: a.oldestDays,
              state: agingState(a.oldestDays).label,
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(AGING_FILTERS, table.filters),
          count: accounts.length,
          total: accounts.length,
          truncated: false,
          unit: 'العملاء',
        }),
      },
    ],
  });

  if (aging.isError) {
    return (
      <div className="page">
        <ErrorState error={aging.error} onRetry={() => void aging.refetch()} />
      </div>
    );
  }

  const data = aging.data;
  const total = data?.totalOutstanding ?? 0;
  const overdue = (data?.buckets ?? [])
    .filter((b) => b.key !== 'current')
    .reduce((sum, b) => sum + b.amount, 0);

  return (
    <div className="page">
      <PageHead
        title="تقادم الذمم"
        subtitle="المستحقات موزّعة على فترات التأخير"
      />

      <div className="stats">
        <Stat label="إجمالي المستحق" value={<span className="num">{money(total)}</span>} />
        <Stat
          label="المتأخر عن موعده"
          value={
            <span className="num" style={{ color: overdue > 0 ? 'var(--danger)' : undefined }}>
              {money(overdue)}
            </span>
          }
          note={total > 0 ? `${Math.round((overdue / total) * 100)}٪ من المستحق` : undefined}
        />
        <Stat label="عملاء عليهم مستحقات" value={num(data?.accounts.length ?? 0)} />
      </div>

      {/*
        الشريط يعرض التوزيع قبل الأرقام.
        النسبة بين الفترات هي المعلومة الأهم: مستحق حديث كله وضع سليم،
        ومستحق متأخر كله وضع يستدعي إجراءً.
      */}
      {total > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="aging-bar mb-4">
              {(data?.buckets ?? []).map((b) =>
                b.amount > 0 ? (
                  <div
                    key={b.key}
                    className="aging-seg"
                    style={{
                      width: `${(b.amount / total) * 100}%`,
                      background: BUCKET_COLOR[b.key] ?? 'var(--ink-4)',
                    }}
                    title={`${b.label}: ${money(b.amount)}`}
                  />
                ) : null,
              )}
            </div>

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الفترة</th>
                    <th>عدد الفواتير</th>
                    <th>المبلغ</th>
                    <th>النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.buckets ?? []).map((b) => (
                    <tr key={b.key}>
                      <td>
                        <span className="row">
                          <span
                            className="dot"
                            style={{ background: BUCKET_COLOR[b.key], width: 8, height: 8 }}
                            aria-hidden
                          />
                          {b.label}
                        </span>
                      </td>
                      <td className="num">{num(b.count)}</td>
                      <td className="num strong">{money(b.amount)}</td>
                      <td className="num cell-mute">
                        {total > 0 ? `${Math.round((b.amount / total) * 100)}٪` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث باسم العميل"
        />
        <ExportMenu build={buildExport} disabled={!accounts.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">العملاء حسب المستحق</h2>
          <span className="small mute" style={{ marginInlineStart: 'auto' }}>
            {table.sort ? 'بالفرز المختار' : 'الأكبر مستحقًا أولًا'}
          </span>
        </div>

        {aging.isPending ? (
          <div className="card-body">
            <div className="skeleton" style={{ height: 120 }} />
          </div>
        ) : data?.accounts.length === 0 ? (
          <div className="card-body">
            <p className="mute small center">لا توجد مستحقات على أي عميل.</p>
          </div>
        ) : (
          <FilterScope {...table.scope}>
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>
                    العميل
                    <HeaderFilter colKey="name" />
                  </th>
                  <th>
                    المستحق
                    <HeaderFilter colKey="outstanding" />
                  </th>
                  <th>
                    أقدم تأخير
                    <HeaderFilter colKey="oldestDays" />
                  </th>
                  <th>
                    الحالة
                    <HeaderFilter colKey="state" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => {
                  const st = agingState(a.oldestDays);
                  return (
                    <tr key={a.accountId}>
                      <td className="cell-strong">
                        <Link to={`/crm/accounts/${a.accountId}`}>{a.name}</Link>
                      </td>
                      <td className="num strong">{money(a.outstanding)}</td>
                      <td className="num cell-mute">
                        {a.oldestDays > 0 ? plural(a.oldestDays, WORDS.day) : 'لم يحن'}
                      </td>
                      <td>
                        <Badge kind={st.kind}>{st.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {accounts.length === 0 && (
              <div className="card-body">
                <p className="mute small center">لا عميل يطابق هذا البحث أو الفلتر.</p>
              </div>
            )}
          </div>
          </FilterScope>
        )}
      </div>
    </div>
  );
}
